import React, { useState, useEffect, Fragment } from "react";
import { Grid, notification } from "antd";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import moment from "moment";
import { useLocation } from "react-router-dom";

import { Button, Box, Pagination, Select } from "components";
import {
  DownloadIcon,
  DownArrow,
  InformationIcon,
  ConfirmFailIcon,
} from "icons";
import {
  handleConvertDateYear,
  localStorageHelper,
  generateDynamicRecords,
  generateCSV,
  mplusFont,
  convertFilename,
} from "utils";
import { IOption } from "components/select/Select";

import { FullStressTypeInfoData } from "models/stressCheck/fullStressInformation";
import { MiniStressTypeInfoData } from "models/stressCheck/miniStressInformation";
import { IFullStressTypeDownloadPdfData } from "models/stressCheck/fullStressTypeDownloadPdf";
import { IMiniStressTypeDownloadPdfData } from "models/stressCheck/miniStressTypeDownloadPdf";
import {
  MiniStressCheckTypeInfoActionCreators,
  FullStressCheckTypeInfoActionCreators,
  DownloadFullStressPdfActionCreators,
  DownloadFullStressCsvActionCreators,
  DownloadMiniStressPdfActionCreators,
  DownloadMiniStressCsvActionCreators,
  FullStressScoringActionCreators,
  MiniStressScoringActionCreators,
  GetFullStressLastNotificationTimeActionCreators,
  GetMiniStressLastNotificationTimeActionCreators,
} from "redux/rootActions";
import {
  selectIsLoadingFullStress,
  selectFullStressErrorMessage,
  fullStressCheckTypeInfoDataResponse,
} from "redux/stressCheck/fullStressTypeInfo/fullStressTypeInfoStates";
import {
  selectIsLoadingMiniStress,
  selectMiniStressErrorMessage,
  miniStressCheckTypeInfoDataResponse,
} from "redux/stressCheck/miniStressTypeInfo/miniStressTypeInfoStates";
import {
  fullStressPdfDownloadSuccessResponse,
  fullStressPdfDownloadErrorMessage,
} from "redux/stressCheck/fullStressDownloadPdf/fullStressDownloadPdfStates";
import {
  fullStressCsvDownloadSuccessResponse,
  fullStressCsvDownloadErrorMessage,
} from "redux/stressCheck/fullStressDownloadCsv/fullStressDownloadCsvStates";
import {
  miniStressPdfDownloadSuccessResponse,
  miniStressPdfDownloadErrorMessage,
} from "redux/stressCheck/miniStressDownloadPdf/miniStressDownloadPdfStates";
import {
  miniStressCsvDownloadSuccessResponse,
  miniStressCsvDownloadErrorMessage,
} from "redux/stressCheck/miniStressDownloadCsv/miniStressDownloadCsvStates";
import { fullStressLastSendNotificationTimeResponse } from "redux/stressCheck/fullStressLastNotificationTime/fullStressLastNotificationTimeStates";
import { miniStressLastSendNotificationTimeResponse } from "redux/stressCheck/miniStressLastNotificationTime/miniStressLastNotificationTimeStates";

import { CheckboxStyled } from "screens/account/Account.style";
import {
  PaginationWrapper,
  ContentWrapper,
  SituationTableStyled,
  WrapperSizeChange,
  TextTitle,
  DownloadButtonsContainer,
  TransmissionTitleContainer,
  SelectionSection,
  WapperSelectTimeForItem,
  CheckboxBlock,
  CheckboxItemWrapper,
  LineCheckboxWrapper,
  PaginationContainer,
  NotifyWrapper,
  CheckboxText,
  Image,
  InfoWrapper,
} from "./Stress.style";
import ConfirmInterviewModal from "./ConfirmInterviewModal";
import StressSituationDetails from "./StressSituationDetails";
import { AllDataForFilters } from "./constant";
import { ConfirmModal } from "./components";

type ScreenStatus = "details" | "list" | "";

const { useBreakpoint } = Grid;

const StressSituation: React.FC = () => {
  const currentLanguage = localStorageHelper.getItem("i18nextLng");
  const { t }: any = useTranslation("translation", {
    keyPrefix: "stress-check",
  });
  const { t: t1 }: any = useTranslation("translation", {
    keyPrefix: "mental-health",
  });
  const { t: t2 }: any = useTranslation("translation", {
    keyPrefix: "user-list",
  });
  const { t: t3 }: any = useTranslation("translation", {
    keyPrefix: "body-temperature",
  });
  const { t: t4 }: any = useTranslation("translation", {
    keyPrefix: "message-delivery",
  });
  const { t: t5 }: any = useTranslation("translation", {
    keyPrefix: "change-student-affiliation",
  });

  const screens = useBreakpoint();
  const location = useLocation();
  const dispatch = useDispatch();

  const isLoadingFullStress = useSelector(selectIsLoadingFullStress);
  const isLoadingMiniStress = useSelector(selectIsLoadingMiniStress);
  const FullStressTypeInformation = useSelector(
    fullStressCheckTypeInfoDataResponse
  );
  const MiniStressTypeInformation = useSelector(
    miniStressCheckTypeInfoDataResponse
  );
  const MiniStressTypeInformationError = useSelector(
    selectFullStressErrorMessage
  );
  const FullStressTypeInformationError = useSelector(
    selectMiniStressErrorMessage
  );
  const fullStressPdfDownloadSuccess = useSelector(
    fullStressPdfDownloadSuccessResponse
  );
  const fullStressCsvDownloadSuccess = useSelector(
    fullStressCsvDownloadSuccessResponse
  );
  const miniStressPdfDownloadSuccess = useSelector(
    miniStressPdfDownloadSuccessResponse
  );
  const miniStressCsvDownloadSuccess = useSelector(
    miniStressCsvDownloadSuccessResponse
  );
  const fullStressDownloadPdfError = useSelector(
    fullStressPdfDownloadErrorMessage
  );
  const miniStressDownloadPdfError = useSelector(
    miniStressPdfDownloadErrorMessage
  );
  const fullStressDownloadCsvError = useSelector(
    fullStressCsvDownloadErrorMessage
  );
  const miniStressDownloadCsvError = useSelector(
    miniStressCsvDownloadErrorMessage
  );
  const fullStressLastNotificationTimeData = useSelector(
    fullStressLastSendNotificationTimeResponse
  );
  const miniStressLastNotificationTimeData = useSelector(
    miniStressLastSendNotificationTimeResponse
  );
  const { monthOptions, yearOptions, currentYear, currentMonth } =
    AllDataForFilters();

  const [screenStatus, setScreenStatus] = useState<ScreenStatus>("list");
  const [displayInfoTop, setDisplayInfoTop] = useState<boolean>(false);
  const [displayInfoBottom, setDisplayInfoBottom] = useState<boolean>(false);
  const [sendHighStressNoti, setSendHighStressNoti] = useState<boolean>(false);
  const [isCheckAll, setIsCheckAll] = useState<boolean>(false);
  const [remindAllNonUsers, setRemindAllNonUsers] = useState<boolean>(false);
  const [checkBoxList, setCheckBoxList] = useState<any>([]);
  const [sendUserList, setSendUserList] = useState<any>([]);
  const [recordOptions, setRecordOptions] = useState<IOption[]>([]);
  const [confirmModalVisible, setConfirmModalVisible] =
    useState<boolean>(false);
  const [remindModalVisible, setRemindModalVisible] = useState<boolean>(false);
  const [isRemindAll, setIsRemindAll] = useState(false);
  const [input, setInput] = useState({
    limit: 50,
    page: 1,
  });
  const [checkFilters, setCheckFilters] = useState<any>({
    not_implemented: false,
    need_attention: false,
    interview_request_sent: false,
    interview_request_not_sent: false,
    high_stress_1: false,
    high_stress_2: false,
    start_year: currentYear,
    start_month: currentMonth,
    end_year: currentYear,
    end_month: currentMonth,
  });
  const [data, setData] = useState<any>({
    list: [],
    users: [],
    total: 0,
  });

  const isFullStressCheck = React.useMemo(() => {
    return location.pathname === "/stress-59-situation";
  }, [location.pathname]);

  const returnStartDate = () => {
    return moment(
      new Date(`${checkFilters.start_year}-${checkFilters.start_month}`)
    )
      .startOf("month")
      .format("YYYY-MM-DD");
  };

  const returnEndDate = () => {
    return moment(
      new Date(`${checkFilters.end_year}-${checkFilters.end_month}`)
    )
      .endOf("month")
      .format("YYYY-MM-DD");
  };

  const getStressTypeInformation = () => {
    const miniStressParams: MiniStressTypeInfoData = {
      page: input.page,
      per_page: input.limit,
      not_implement: checkFilters.not_implemented,
      start_date: returnStartDate(),
      end_date: returnEndDate(),
      need_attention: checkFilters.need_attention,
      high_stress_1: checkFilters.high_stress_1,
      high_stress_2: checkFilters.high_stress_2,
      locale: currentLanguage,
    };
    const fullStressParams: FullStressTypeInfoData = {
      ...miniStressParams,
      interview_request_sent: checkFilters.interview_request_sent,
      interview_request_not_sent: checkFilters.interview_request_not_sent,
    };

    if (isFullStressCheck) {
      dispatch(
        FullStressCheckTypeInfoActionCreators.fullStressCheckTypeInfoAction(
          fullStressParams
        )
      );
    } else {
      dispatch(
        MiniStressCheckTypeInfoActionCreators.miniStressCheckTypeInfoAction(
          miniStressParams
        )
      );
    }
  };

  const getLastReminderTime = () => {
    if (isFullStressCheck) {
      dispatch(
        GetFullStressLastNotificationTimeActionCreators.getFullStressLastNotificationTimeAction()
      );
    } else {
      dispatch(
        GetMiniStressLastNotificationTimeActionCreators.getMiniStressLastNotificationTimeAction()
      );
    }
  };

  const handleTypeInfoDownloadFile = (type: string) => {
    const miniStressParams: IMiniStressTypeDownloadPdfData = {
      not_implement: checkFilters.not_implemented,
      start_date: returnStartDate(),
      end_date: returnEndDate(),
      need_attention: checkFilters.need_attention,
      high_stress_1: checkFilters.high_stress_1,
      high_stress_2: checkFilters.high_stress_2,
    };
    const fullStressParams: IFullStressTypeDownloadPdfData = {
      ...miniStressParams,
      interview_request_sent: checkFilters.interview_request_sent,
      interview_request_not_sent: checkFilters.interview_request_not_sent,
    };
    if (type === "pdf") {
      if (isFullStressCheck) {
        dispatch(
          DownloadFullStressPdfActionCreators.fullStressPdfDownloadAction(
            fullStressParams
          )
        );
      } else {
        dispatch(
          DownloadMiniStressPdfActionCreators.miniStressPdfDownloadAction(
            miniStressParams
          )
        );
      }
    } else {
      if (isFullStressCheck) {
        dispatch(
          DownloadFullStressCsvActionCreators.fullStressCsvDownloadAction(
            fullStressParams
          )
        );
      } else {
        dispatch(
          DownloadMiniStressCsvActionCreators.miniStressCsvDownloadAction(
            miniStressParams
          )
        );
      }
    }
  };

  const onChangeCheckAll = (e: any) => {
    setIsCheckAll(e.target.checked);
    if (e.target.checked) {
      setCheckBoxList(
        data.list
          .filter((item: any) => !item.notifyable_id)
          .map((i: any) => {
            return {
              user_id: i.user_id,
              full_name: i.name,
              stress_situation: i.stress_situation,
            };
          })
      );
    } else {
      setCheckBoxList([]);
    }
  };

  const onChangeCheckbox =
    (id: string, name: string, situation: string) => (e: any) => {
      if (e.target.checked) {
        //add item
        setCheckBoxList((prevState: any) => [
          ...prevState,
          {
            user_id: id,
            full_name: name,
            stress_situation: situation,
          },
        ]);
      } else {
        //remove item
        setCheckBoxList(
          checkBoxList.filter((item: any) => item.user_id !== id)
        );
      }
    };

  useEffect(() => {
    if (data.list.length) {
      const numberUserNonSend = data.list.filter(
        (item: any) => !item.notifyable_id
      ).length;
      if (checkBoxList.length === numberUserNonSend) {
        setIsCheckAll(true);
      } else {
        setIsCheckAll(false);
      }
    }
  }, [checkBoxList]);

  useEffect(() => {
    if (
      FullStressTypeInformation &&
      Object.keys(FullStressTypeInformation).length
    ) {
      setData({
        list: FullStressTypeInformation?.result?.full_stress_result,
        total: FullStressTypeInformation?.total_record,
        // checkUsers: FullStressTypeInformation?.result?.stress_check_users,
      });
    }
  }, [FullStressTypeInformation]);

  useEffect(() => {
    if (
      MiniStressTypeInformation &&
      Object.keys(MiniStressTypeInformation).length
    ) {
      setData({
        list: MiniStressTypeInformation?.result?.mini_stress_result,
        total: MiniStressTypeInformation?.total_record,
        // checkUsers: MiniStressTypeInformation?.result?.stress_check_users,
      });
    }
  }, [MiniStressTypeInformation]);

  useEffect(() => {
    if (FullStressTypeInformationError) {
      notification.error({
        placement: "bottomRight",
        message: FullStressTypeInformationError,
        icon: <ConfirmFailIcon width={53} height={53} />,
      });
      dispatch(FullStressCheckTypeInfoActionCreators.handleResetDataAction());
      setData({
        list: [],
        total: 0,
        // checkUsers: [],
      });
    }
  }, [FullStressTypeInformationError]);

  useEffect(() => {
    if (
      FullStressTypeInformation &&
      Object.keys(FullStressTypeInformation).length
    ) {
      let recordOptionsClone: IOption[] = [];
      recordOptionsClone = generateDynamicRecords(
        FullStressTypeInformation?.total_record,
        t2
      );
      setRecordOptions([...recordOptionsClone]);
    }
  }, [FullStressTypeInformation]);

  useEffect(() => {
    getStressTypeInformation();
    getLastReminderTime();
  }, []);

  useEffect(() => {
    if (
      MiniStressTypeInformation &&
      Object.keys(MiniStressTypeInformation).length
    ) {
      let recordOptionsClone: IOption[] = [];
      recordOptionsClone = generateDynamicRecords(
        MiniStressTypeInformation?.total_record,
        t2
      );
      setRecordOptions([...recordOptionsClone]);
    }
  }, [MiniStressTypeInformation]);

  useEffect(() => {
    if (MiniStressTypeInformationError) {
      notification.error({
        placement: "bottomRight",
        message: MiniStressTypeInformationError,
        icon: <ConfirmFailIcon width={53} height={53} />,
      });
      dispatch(MiniStressCheckTypeInfoActionCreators.handleResetDataAction());
      setData({
        list: [],
        total: 0,
        // checkUsers: [],
      });
    }
  }, [MiniStressTypeInformationError]);

  useEffect(() => {
    if (
      fullStressDownloadPdfError ||
      fullStressDownloadCsvError ||
      miniStressDownloadCsvError ||
      miniStressDownloadPdfError
    ) {
      notification.error({
        placement: "bottomRight",
        message:
          fullStressDownloadPdfError ||
          fullStressDownloadCsvError ||
          miniStressDownloadCsvError ||
          miniStressDownloadPdfError,
        icon: <ConfirmFailIcon width={53} height={53} />,
      });
      dispatch(DownloadFullStressPdfActionCreators.handleResetDataAction());
      dispatch(DownloadFullStressCsvActionCreators.handleResetDataAction());
      dispatch(DownloadMiniStressPdfActionCreators.handleResetDataAction());
      dispatch(DownloadMiniStressCsvActionCreators.handleResetDataAction());
    }
  }, [
    fullStressDownloadPdfError,
    fullStressDownloadCsvError,
    miniStressDownloadCsvError,
    miniStressDownloadPdfError,
  ]);

  useEffect(() => {
    if (fullStressPdfDownloadSuccess && fullStressPdfDownloadSuccess.result) {
      const headers = fullStressPdfDownloadSuccess.result.headers.map(
        (header: any) => {
          return header.label;
        }
      );
      const data: any = fullStressPdfDownloadSuccess.result.data
        .map((item: any) => {
          return {
            ...item,
            mental_condition: item.mental_condition,
          };
        })
        .map((d: any) => {
          return Object.values(d);
        });

      const doc = new jsPDF();
      doc.addFileToVFS("mplus.ttf", mplusFont);
      doc.addFont("mplus.ttf", "mplus", "normal");
      doc.setFont("mplus", "normal");

      const myTable = {
        head: [[...headers]],
        body: data,
        headStyles: {
          font: "mplus",
        },
        bodyStyles: {
          font: "mplus",
        },
        margin: { top: 15, left: 8, right: 8 },
      };

      autoTable(doc, myTable);
      const filename = `${convertFilename("Full_stress", new Date())}.pdf`;
      doc.save(filename);
      dispatch(DownloadFullStressPdfActionCreators.handleResetDataAction());
    }
  }, [fullStressPdfDownloadSuccess]);

  useEffect(() => {
    if (miniStressPdfDownloadSuccess && miniStressPdfDownloadSuccess.result) {
      const headers = miniStressPdfDownloadSuccess.result.headers.map(
        (header: any) => {
          return header.label;
        }
      );
      const data: any = miniStressPdfDownloadSuccess.result.data
        .map((item: any) => {
          return {
            ...item,
            mental_condition: item.mental_condition,
          };
        })
        .map((d: any) => {
          return Object.values(d);
        });

      const doc = new jsPDF();
      doc.addFileToVFS("mplus.ttf", mplusFont);
      doc.addFont("mplus.ttf", "mplus", "normal");
      doc.setFont("mplus", "normal");

      const myTable = {
        head: [[...headers]],
        body: data,
        headStyles: {
          font: "mplus",
        },
        bodyStyles: {
          font: "mplus",
        },
        margin: { top: 15, left: 8, right: 8 },
      };

      autoTable(doc, myTable);
      const filename = `${convertFilename("Mini_stress", new Date())}.pdf`;
      doc.save(filename);
      dispatch(DownloadMiniStressPdfActionCreators.handleResetDataAction());
    }
  }, [miniStressPdfDownloadSuccess]);

  useEffect(() => {
    if (fullStressCsvDownloadSuccess && fullStressCsvDownloadSuccess.result) {
      const filename = `${convertFilename("Full_stress", new Date())}.csv`;
      generateCSV(
        filename,
        fullStressCsvDownloadSuccess.result.headers,
        fullStressCsvDownloadSuccess.result.data.map((item: any) => {
          return {
            ...item,
            mental_condition: item.mental_condition,
          };
        })
      );
      dispatch(DownloadFullStressCsvActionCreators.handleResetDataAction());
    }
  }, [fullStressCsvDownloadSuccess]);

  useEffect(() => {
    if (miniStressCsvDownloadSuccess && miniStressCsvDownloadSuccess.result) {
      const filename = `${convertFilename("Mini_stress", new Date())}.csv`;
      generateCSV(
        filename,
        miniStressCsvDownloadSuccess.result.headers,
        miniStressCsvDownloadSuccess.result.data.map((item: any) => {
          return {
            ...item,
            mental_condition: item.mental_condition,
          };
        })
      );
      dispatch(DownloadMiniStressCsvActionCreators.handleResetDataAction());
    }
  }, [miniStressCsvDownloadSuccess]);

  useEffect(() => {
    dispatch(FullStressCheckTypeInfoActionCreators.handleResetDataAction());
    dispatch(MiniStressCheckTypeInfoActionCreators.handleResetDataAction());
    dispatch(FullStressScoringActionCreators.handleResetAction());
    dispatch(MiniStressScoringActionCreators.handleResetAction());
    dispatch(
      GetFullStressLastNotificationTimeActionCreators.handleResetDataAction()
    );
    dispatch(
      GetMiniStressLastNotificationTimeActionCreators.handleResetDataAction()
    );
    setRecordOptions([]);
    setCheckBoxList([]);
    setSendHighStressNoti(false);
    setRemindAllNonUsers(false);
    setSendUserList([]);
  }, []);

  useEffect(() => {
    setData({
      list: [],
      users: [],
      // checkUsers: [],
      total: 0,
    });
    setIsCheckAll(false);
    setCheckBoxList([]);
  }, [location.pathname]);

  const handleCancel = () => {
    setConfirmModalVisible(false);
    setSendHighStressNoti(false);
    setRemindAllNonUsers(false);
    setCheckBoxList([]);
    setSendUserList([]);
    getStressTypeInformation();
  };

  const handleReturn = () => {
    setScreenStatus("list");
    setConfirmModalVisible(false);
  };

  const returnStatus = (status?: string) => {
    switch (status) {
      case "未実施":
        return "#B7B7B7";
      case "低ストレス":
        return "#3B3B3B";
      case "要注意":
        return "#EBC300";
      case "高ストレス":
        return "#FB4E4E";
    }
  };
  const returnTitle = (status?: string) => {
    if (currentLanguage === "vi") {
      switch (status) {
        case "未実施":
          return "Chưa thực hiện";
        case "低ストレス":
          return "Stress thấp";
        case "要注意":
          return "Chú ý";
        case "高ストレス":
          return "Stress Cao";
      }
    } else {
      return status;
    }
  };

  const returnGender = (gender?: string) => {
    if (currentLanguage === "vi") {
      if (gender === "男性") {
        return "Nam";
      } else if (gender === "女性") {
        return "Nữ";
      } else {
        return "Chưa trả lời";
      }
    } else {
      return gender;
    }
  };

  const columns = [
    {
      title: () => {
        return (
          <CheckboxText>
            <CheckboxStyled checked={isCheckAll} onChange={onChangeCheckAll} />
            <div>{t2("full-name")}</div>
          </CheckboxText>
        );
      },
      dataIndex: "name",
      key: "name",
      width: 132,
      fixed: window.innerWidth < 576 ? "" : "left",
      show: true,
      render: (text: string, record: any) => {
        return (
          <CheckboxText>
            {!record?.notifyable_id ? (
              <CheckboxStyled
                checked={checkBoxList.some(
                  (item: any) => item.user_id === record?.user_id
                )}
                onChange={onChangeCheckbox(
                  record?.user_id,
                  record?.name,
                  record?.stress_situation
                )}
              />
            ) : (
              <div style={{ width: 20, height: 20 }}></div>
            )}
            <div>{text}</div>
          </CheckboxText>
        );
      },
    },
    {
      title: t1("implementation-date"),
      dataIndex: "date_of_implementation",
      key: "date_of_implementation",
      width: 132,
      show: true,
      render: (text: string) => {
        if (!text) {
          return <div>-</div>;
        }
        return <div>{text}</div>;
      },
    },
    {
      title: t("stress-situation"),
      dataIndex: "stress_situation",
      key: "stress_situation",
      width: 146,
      show: true,
      render: (text: string, record: any) => {
        return (
          <div
            style={{
              color: returnStatus(record?.stress_situation),
              fontWeight:
                record?.stress_situation === "高ストレス" ||
                record?.stress_situation === "要注意"
                  ? "bold"
                  : "normal",
            }}
          >
            {returnTitle(text)}
          </div>
        );
      },
    },
    {
      title: t1("status"),
      dataIndex: "status",
      key: "status",
      width: 130,
      show: isFullStressCheck,
      render: (text: string, record: any) => {
        return (
          <div
            style={{
              color: returnStatus(record?.stress_situation),
              fontWeight:
                record?.stress_situation === "高ストレス" ||
                record?.stress_situation === "要注意"
                  ? "bold"
                  : "normal",
            }}
          >
            {text}
          </div>
        );
      },
    },
    {
      title: () => {
        return (
          <div>
            {t("transmission-status-1")}
            <div>{t("transmission-status-2")}</div>
          </div>
        );
      },
      dataIndex: "interview_transmission_status",
      key: "interview_transmission_status",
      width: 114,
      show: isFullStressCheck,
    },
    {
      title: () => {
        return <div style={{ fontWeight: "bold" }}>B</div>;
      },
      dataIndex: "B",
      key: "B",
      width: 71,
      show: true,
      render: (text: string, record: any) => {
        return (
          <div
            style={{
              color: returnStatus(record?.stress_situation),
              fontWeight:
                record?.stress_situation === "高ストレス" ||
                record?.stress_situation === "要注意"
                  ? "bold"
                  : "normal",
            }}
          >
            {text}
          </div>
        );
      },
    },
    {
      title: () => {
        return <div style={{ fontWeight: "bold" }}>A+C</div>;
      },
      dataIndex: "A_C",
      key: "A_C",
      width: 81,
      show: true,
      render: (text: string, record: any) => {
        return (
          <div
            style={{
              color: record?.A_C === 15 ? "#FB4E4E" : "#3B3B3B",
              fontWeight: record?.A_C === 15 ? "bold" : "normal",
            }}
          >
            {text}
          </div>
        );
      },
    },
    {
      title: t4("belongs"),
      dataIndex: "affiliation",
      key: "affiliation",
      width: 94,
      show: true,
    },
    {
      title: t2("gender"),
      dataIndex: "gender",
      key: "gender",
      width: 82,
      show: true,
      render: (text: string) => {
        if (!text) {
          return <div>-</div>;
        }
        return <div>{returnGender(text)}</div>;
      },
    },
    {
      title: "",
      dataIndex: "",
      key: "",
      width: 162,
      show: true,
      render: (record: any) =>
        record?.stress_situation !== "未実施" ? (
          <div style={{ height: 30 }}></div>
        ) : (
          <Button
            color="#FFFFFF"
            fontSize={16}
            fontWeight={700}
            lineHeight="23px"
            background="#2AC769"
            padding="4px 16px 3px"
            name={
              currentLanguage === "vi" ? "Chi tiết" : t1("scoring-information")
            }
            bdr="6px"
            border="none"
            onClick={() => {
              if (isFullStressCheck) {
                dispatch(
                  FullStressScoringActionCreators.getFullStressScoringAction({
                    stress_check_id: record?.stress_check_id,
                  })
                );
              } else {
                dispatch(
                  MiniStressScoringActionCreators.getMiniStressScoringAction({
                    stress_check_id: record?.stress_check_id,
                  })
                );
              }
              setScreenStatus("details");
            }}
          />
        ),
    },
  ].filter((column: any) => column.show);

  const handleChangePage = (page: number) => {
    setInput((prevState: any) => ({ ...prevState, page }));
  };

  const handleOnChangeSizeChange = (limit: number) => {
    setInput((prevState: any) => ({ ...prevState, limit, page: 1 }));
  };

  const DownloadMenu = () => (
    <DownloadButtonsContainer>
      <Button
        icon={
          <DownloadIcon
            width="14px"
            height="14px"
            fill="currentColor"
            style={{ position: "absolute", left: 4, top: 4 }}
          />
        }
        name={t2("excel-output")}
        background="#2AC769"
        color="#FFFFFF"
        border="none"
        fontSize={12}
        lineHeight="17px"
        fontWeight={700}
        padding="3px 8px 3px 14px"
        bdr="4px"
        onClick={() => handleTypeInfoDownloadFile("csv")}
      />
      <Button
        icon={
          <DownloadIcon
            width="14px"
            height="14px"
            fill="currentColor"
            style={{ position: "absolute", left: 4, top: 4 }}
          />
        }
        name={t2("pdf-output")}
        background="#2AC769"
        color="#FFFFFF"
        border="none"
        fontSize={12}
        lineHeight="17px"
        fontWeight={700}
        padding="3px 8px 3px 14px"
        bdr="4px"
        onClick={() => handleTypeInfoDownloadFile("pdf")}
      />
    </DownloadButtonsContainer>
  );

  const ActionSection = () => {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          padding: "8px 8px 9px",
        }}
      >
        <NotifyWrapper fullStressCheck={isFullStressCheck}>
          {!screens.xs && <DownArrow />}
          <div className="notify-title">{t1("checked-user")}</div>
          <div className="button-wrapper">
            {isFullStressCheck && (
              <Button
                color="#FFFFFF"
                fontSize={12}
                fontWeight={700}
                lineHeight="12px"
                background="#2AC769"
                padding="6px 12px 5px"
                name={t1("send-interview-confirm-notice")}
                bdr="6px 0px 0px 6px"
                border="none"
                disabled={checkBoxList.every(
                  (item: any) => item.stress_situation == "未実施"
                )}
                onClick={() => {
                  setSendHighStressNoti(true);
                  setConfirmModalVisible(true);
                }}
              />
            )}
            <Button
              color="#FFFFFF"
              fontSize={12}
              fontWeight={700}
              lineHeight="12px"
              background="#666666"
              padding="6px 12px 5px"
              name={t1("send-a-reminder")}
              bdr={isFullStressCheck ? "0px 6px 6px 0px" : "6px"}
              border="none"
              disabled={checkBoxList.every(
                (item: any) => item.stress_situation !== "未実施"
              )}
              onClick={() => {
                setConfirmModalVisible(true);
              }}
            />
          </div>
        </NotifyWrapper>
        {!screens.xs && <DownloadMenu />}
      </div>
    );
  };

  const ShowPagination = () => {
    return data.total > input.limit ? (
      <PaginationWrapper>
        <Pagination
          current={input.page}
          onChange={handleChangePage}
          pageSize={input.limit}
          total={data.total}
          showSizeChanger={false}
          showLessItems={!screens.xl}
        />
      </PaginationWrapper>
    ) : (
      <Fragment></Fragment>
    );
  };

  const renderContents = () => {
    switch (screenStatus) {
      case "list":
        return (
          <Box
            title={
              isFullStressCheck
                ? t("implementation-status-59")
                : t("implementation-status-25")
            }
            padding="24px 16px 32px"
          >
            <TransmissionTitleContainer>
              <div>
                {t1("last-reminder-time")}
                {fullStressLastNotificationTimeData?.result
                  ?.last_reminder_notification_at &&
                  `${handleConvertDateYear(
                    moment(
                      fullStressLastNotificationTimeData?.result
                        ?.last_reminder_notification_at
                    ).format("YYYY-MM-DD"),
                    currentLanguage
                  )} ${moment(
                    fullStressLastNotificationTimeData?.result
                      ?.last_reminder_notification_at
                  ).format("HH:MM")}`}
                {miniStressLastNotificationTimeData?.result
                  ?.last_reminder_notification_at &&
                  `${handleConvertDateYear(
                    moment(
                      miniStressLastNotificationTimeData?.result
                        ?.last_reminder_notification_at
                    ).format("YYYY-MM-DD"),
                    currentLanguage
                  )} ${moment(
                    miniStressLastNotificationTimeData?.result
                      ?.last_reminder_notification_at
                  ).format("HH:MM")}`}
              </div>
              <Button
                color="#FFFFFF"
                fontSize={14}
                fontWeight={700}
                lineHeight="14px"
                background={"#666666"}
                padding="8px 16px 7px"
                name={t1("send-implementation-reminder-all")}
                bdr="6px"
                border="none"
                disabled={data.list.every(
                  (item: any) => item.stress_situation !== "未実施"
                )}
                onClick={() => {
                  setConfirmModalVisible(true);
                  setIsRemindAll(true);
                  setSendUserList(
                    data.list
                      .filter((item: any) => item.stress_situation === "未実施")
                      .map((i: any) => {
                        return {
                          user_id: i.user_id,
                          full_name: i.name,
                        };
                      })
                  );
                }}
              />
            </TransmissionTitleContainer>
            <SelectionSection>
              <div className="first-block">
                <WapperSelectTimeForItem>
                  <TextTitle style={{ marginBottom: 16 }}>
                    {t("select-implementation-period")}
                  </TextTitle>
                  <div className="bor-div-time">
                    <div className="dleft">
                      <Select
                        options={yearOptions}
                        width={"100px"}
                        placeholder="00"
                        height="30px"
                        value={checkFilters.start_year}
                        onChange={(e: any) => {
                          setCheckFilters({
                            ...checkFilters,
                            start_year: e,
                          });
                        }}
                      />
                      <Select
                        options={monthOptions}
                        width={"82px"}
                        placeholder="00"
                        height="30px"
                        marginForm="0px 0px 0px 8px"
                        value={checkFilters.start_month}
                        onChange={(e: any) => {
                          setCheckFilters({
                            ...checkFilters,
                            start_month: e,
                          });
                        }}
                      />
                      <span style={{ padding: "0px 8px", lineHeight: "30px" }}>
                        〜
                      </span>
                    </div>
                    <div className="dselectright">
                      <Select
                        options={yearOptions}
                        width={"100px"}
                        placeholder="00"
                        height="30px"
                        value={checkFilters.end_year}
                        onChange={(e: any) => {
                          setCheckFilters({
                            ...checkFilters,
                            end_year: e,
                          });
                        }}
                      />
                      <Select
                        options={monthOptions}
                        width={"82px"}
                        placeholder="00"
                        height="30px"
                        marginForm="0px 0px 0px 8px"
                        value={checkFilters.end_month}
                        onChange={(e: any) => {
                          setCheckFilters({
                            ...checkFilters,
                            end_month: e,
                          });
                        }}
                      />
                    </div>
                  </div>
                </WapperSelectTimeForItem>
                <Button
                  color="#FFFFFF"
                  fontSize={16}
                  fontWeight={700}
                  lineHeight="23px"
                  background={"#2AC769"}
                  padding="4px 13px 3px"
                  name={t2("search")}
                  bdr="6px"
                  border="none"
                  onClick={getStressTypeInformation}
                />
              </div>
              <div className="second-block">
                <TextTitle style={{ marginBottom: 16 }}>
                  {t1("requirement-selection")}
                </TextTitle>
                <CheckboxBlock>
                  <div className="left">
                    <CheckboxItemWrapper>
                      <CheckboxStyled
                        checked={checkFilters.not_implemented}
                        onChange={() =>
                          setCheckFilters({
                            ...checkFilters,
                            not_implemented: !checkFilters.not_implemented,
                          })
                        }
                      />
                      <div>{t3("not-attempt")}</div>
                    </CheckboxItemWrapper>
                    <CheckboxItemWrapper>
                      <CheckboxStyled
                        checked={checkFilters.need_attention}
                        onChange={() =>
                          setCheckFilters({
                            ...checkFilters,
                            need_attention: !checkFilters.need_attention,
                          })
                        }
                      />
                      <div>{t("be-careful")}</div>
                    </CheckboxItemWrapper>
                  </div>
                  {isFullStressCheck && (
                    <div className="right">
                      <CheckboxItemWrapper>
                        <CheckboxStyled
                          checked={checkFilters.interview_request_sent}
                          onChange={() =>
                            setCheckFilters({
                              ...checkFilters,
                              interview_request_sent:
                                !checkFilters.interview_request_sent,
                            })
                          }
                        />
                        <div>{t1("request-for-interview")}</div>
                      </CheckboxItemWrapper>
                      <CheckboxItemWrapper>
                        <CheckboxStyled
                          checked={checkFilters.interview_request_not_sent}
                          onChange={() =>
                            setCheckFilters({
                              ...checkFilters,
                              interview_request_not_sent:
                                !checkFilters.interview_request_not_sent,
                            })
                          }
                        />
                        <div>{t1("interview-notification-not-sent")}</div>
                      </CheckboxItemWrapper>
                    </div>
                  )}
                </CheckboxBlock>
                <div style={{ marginLeft: 2 }}>
                  <LineCheckboxWrapper style={{ padding: "12px 0px" }}>
                    <CheckboxStyled
                      checked={checkFilters.high_stress_1}
                      onChange={() =>
                        setCheckFilters({
                          ...checkFilters,
                          high_stress_1: !checkFilters.high_stress_1,
                        })
                      }
                    />
                    <div style={{ lineHeight: "26px" }}>
                      {t("high-stress-1-label")}
                      <span
                        style={{
                          fontWeight: currentLanguage === "vi" ? 400 : 500,
                        }}
                      >
                        {t("high-stress-1-content")}
                      </span>
                      <InfoWrapper visible={displayInfoTop}>
                        <InformationIcon
                          style={{
                            marginLeft: 4,
                            position: "relative",
                            top: "4px",
                          }}
                          onMouseOver={() => setDisplayInfoTop(true)}
                          onMouseLeave={() => setDisplayInfoTop(false)}
                        />
                        {displayInfoTop && (
                          <Image src="/images/stress-graph-1.png" />
                        )}
                      </InfoWrapper>
                    </div>
                  </LineCheckboxWrapper>
                  <LineCheckboxWrapper>
                    <CheckboxStyled
                      checked={checkFilters.high_stress_2}
                      onChange={() =>
                        setCheckFilters({
                          ...checkFilters,
                          high_stress_2: !checkFilters.high_stress_2,
                        })
                      }
                    />
                    <div style={{ lineHeight: "26px" }}>
                      {t("high-stress-2-label")}
                      <span
                        style={{
                          fontWeight: currentLanguage === "vi" ? 400 : 500,
                        }}
                      >
                        {t("high-stress-2-content")}
                      </span>
                      <InfoWrapper visible={displayInfoBottom}>
                        <InformationIcon
                          style={{
                            marginLeft: 4,
                            position: "relative",
                            top: "4px",
                          }}
                          onMouseOver={() => setDisplayInfoBottom(true)}
                          onMouseLeave={() => setDisplayInfoBottom(false)}
                        />
                        {displayInfoBottom && (
                          <Image src="/images/stress-graph-1.png" />
                        )}
                      </InfoWrapper>
                    </div>
                  </LineCheckboxWrapper>
                </div>
              </div>
              <div className="third-block">
                <Button
                  color="#FFFFFF"
                  fontSize={16}
                  fontWeight={700}
                  lineHeight="23px"
                  background={"#2AC769"}
                  padding="4px 34px 3px"
                  name={t2("search")}
                  bdr="6px"
                  border="none"
                  onClick={getStressTypeInformation}
                />
              </div>
            </SelectionSection>
            <PaginationContainer
              style={{
                alignItems: "flex-start",
              }}
            >
              <WrapperSizeChange>
                <div style={{ fontWeight: 500 }}>{t2("record")}</div>
                <Select
                  defaultValue={50}
                  options={recordOptions}
                  onChange={handleOnChangeSizeChange}
                />
              </WrapperSizeChange>
              <ShowPagination />
            </PaginationContainer>
            {screens.xs && <DownloadMenu />}
            <ContentWrapper>
              <SituationTableStyled
                dataSource={data.list || []}
                columns={columns}
                actionSection={<ActionSection />}
                scroll={{ x: "max-content" }}
                loading={isLoadingFullStress || isLoadingMiniStress}
              />
              {!FullStressTypeInformation?.result?.full_stress_result?.length &&
                isFullStressCheck && (
                  <div
                    style={{
                      fontWeight: 400,
                      fontSize: 18,
                      lineHeight: "26px",
                      marginTop: 32,
                    }}
                  >
                    <div>{t5("no-data-reflection-list")}</div>
                  </div>
                )}
              {!MiniStressTypeInformation?.result?.mini_stress_result?.length &&
                !isFullStressCheck && (
                  <div
                    style={{
                      fontWeight: 400,
                      fontSize: 18,
                      lineHeight: "26px",
                      marginTop: 32,
                    }}
                  >
                    <div>{t5("no-data-reflection-list")}</div>
                  </div>
                )}
            </ContentWrapper>
            <ShowPagination />

            {confirmModalVisible && (
              <ConfirmInterviewModal
                isVisible={confirmModalVisible}
                handleCancel={handleCancel}
                label={
                  sendHighStressNoti
                    ? t("interview-confirmation-notice")
                    : t("stress-check-reminder")
                }
                checkBoxList={isRemindAll ? sendUserList : checkBoxList}
                setCheckBoxList={
                  isRemindAll ? setSendUserList : setCheckBoxList
                }
                isSendAll={isRemindAll}
                setIsRemindAll={setIsRemindAll}
                isFullStress={isFullStressCheck}
                isHighStress={sendHighStressNoti}
              />
            )}
          </Box>
        );
      case "details":
        return <StressSituationDetails handleCancel={handleReturn} />;
      default:
        return;
    }
  };

  return <>{renderContents()}</>;
};

export default StressSituation;
